import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Reference-counted, iOS-safe body scroll lock — shared by every overlay.
 *
 * Why a singleton with a counter: several overlays can be open at once (menu
 * drawer + search, cart + dialog…). If each overlay set/cleared
 * `body { position: fixed }` independently, the first one to close would unlock
 * while another was still open. And if a close animation was interrupted or
 * absent, the body could stay `position: fixed` forever. On iOS that makes
 * Safari draw the page behind the status/address bars (the "site tronqué en
 * haut/en bas" bug) and removes the scroll bounce.
 *
 * Exposed on `window` so the standalone safety-net script (and Rebuy/LSG carts)
 * can force-reset it without importing this module.
 */
const ScrollLock = {
  _count: 0,
  _scrollY: 0,

  /** Take a lock. The body is fixed only on the first (0 → 1) acquisition. */
  lock() {
    if (this._count === 0) {
      this._scrollY = window.scrollY;
      // Measure scrollability BEFORE fixing the body — once <body> is
      // position:fixed it leaves the flow and scrollHeight collapses to the
      // viewport, which would always read as "not scrollable".
      const pageScrollable =
        document.documentElement.scrollHeight > window.innerHeight;
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this._scrollY}px`;
      document.documentElement.setAttribute('body-scroll-locked', '');
      // Keep the scrollbar visible (inert) while locked so the page doesn't shift
      // on platforms with classic scrollbars. Only when the page actually scrolls
      // — a short page must not gain a bar (which would shift the other way).
      // Paired with `html[keep-scrollbar] { overflow-y: scroll }` in base.css.
      if (pageScrollable) {
        document.documentElement.setAttribute('keep-scrollbar', '');
      }
    }
    this._count += 1;
  },

  /** Release a lock. The body is restored only on the last (1 → 0) release. */
  unlock() {
    if (this._count === 0) return;
    this._count -= 1;
    if (this._count === 0) this._release();
  },

  /** Force-clear regardless of count — used by the safety net / bfcache restore. */
  reset() {
    this._count = 0;
    this._release();
  },

  _release() {
    const wasLocked = document.body.style.position === 'fixed';
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.documentElement.removeAttribute('body-scroll-locked');
    document.documentElement.removeAttribute('keep-scrollbar');
    if (wasLocked) window.scrollTo({ top: this._scrollY, behavior: 'instant' });
  },
};

if (typeof window !== 'undefined') {
  window.ScrollLock = window.ScrollLock || ScrollLock;
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      ScrollLock.lock();

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  async closeDialog() {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Release the body scroll lock NOW — never gate it on the close animation.
    // onAnimationEnd may never resolve if the animation is interrupted or absent
    // (fast re-close, section re-render, element removed), which previously left
    // <body position:fixed> stuck and the page truncated behind the iOS bars.
    // The body was fixed at top:-scrollY, so restoring here is visually seamless.
    ScrollLock.unlock();

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

// `<details scroll-lock>` (the mobile menu drawer) now shares the same
// reference-counted position:fixed lock as every other overlay. The old
// `html { overflow: hidden }` approach collapsed the iOS toolbar and never
// recovered until the next navigation — the menu drawer was the one overlay
// still on it, which is why only it stayed clipped after closing.
function lockDocumentScroll() {
  ScrollLock.lock();
}

function unlockDocumentScroll() {
  ScrollLock.unlock();
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockDocumentScroll();
        } else {
          unlockDocumentScroll();
        }
      }
    }
  },
  { capture: true }
);
